import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  Position,
  useReactFlow,
  type EdgeProps,
  type Edge
} from "@xyflow/react";

export type IECardinality = "1:1" | "0:1" | "1:M" | "0:1:M";

export type IERelationshipEdgeData = {
  label?: string;
  source_cardinality?: IECardinality;
  target_cardinality?: IECardinality;
};

export type IERelationshipEdgeType = Edge<IERelationshipEdgeData, "ieRelationship">;

const EDGE_COLOR = "var(--line-strong, #64748b)";
const EDGE_WIDTH = 2;
const EDGE_WIDTH_HOVER = 3;
const BEZIER_CURVATURE = 0.25;

const BAR_H = 5;
const CIRC_R = 3;
const CROW_LEN = 8;
const CROW_SPREAD = 5;
const INNER_GAP = 5;
const STUB_PAD = 3;
const FAN_SPACING = 16;

// ── Math helpers ──

function posToPerpendicular(pos: Position): [number, number] {
  switch (pos) {
    case Position.Right:
    case Position.Left:
      return [0, 1];
    case Position.Top:
    case Position.Bottom:
      return [1, 0];
  }
}

function fanOffset(index: number, count: number): number {
  if (count <= 1) return 0;
  return (index - (count - 1) / 2) * FAN_SPACING;
}

function posToOutward(pos: Position): [number, number] {
  switch (pos) {
    case Position.Right: return [1, 0];
    case Position.Left:  return [-1, 0];
    case Position.Top:   return [0, -1];
    case Position.Bottom: return [0, 1];
  }
}

function glyphExtent(card: IECardinality): number {
  let w: number;
  switch (card) {
    case "1:1": w = 4; break;
    case "0:1": w = 2 + CIRC_R * 2; break;
    case "1:M": w = CROW_LEN + 2; break;
    case "0:1:M": w = CROW_LEN + 2 + 2 + CIRC_R * 2; break;
  }
  return INNER_GAP + w + STUB_PAD;
}

// ── Cardinality glyphs ──
//
// Local frame: +x = away from entity, y=0 = the line, x=0 = nearest to entity.
//
// Standard IE reading from EACH entity outward along the line:
//   parent -|0---------0|< child
//
// So from entity outward:
//   1:1   → ||    (bar, bar)
//   0:1   → |○    (bar, circle)
//   1:M   → <|    (crow tines touching entity, then bar)
//   0:1:M → <|○   (crow, bar, circle)
//
// Crow's foot: tines fan TOWARD entity (at x≈0), apex AWAY (+x).

type GlyphProps = { color: string; sw: number };

function GlyphExactlyOne({ color, sw }: GlyphProps) {
  return (
    <g>
      <line x1={0} y1={-BAR_H} x2={0} y2={BAR_H} stroke={color} strokeWidth={sw} />
      <line x1={4} y1={-BAR_H} x2={4} y2={BAR_H} stroke={color} strokeWidth={sw} />
    </g>
  );
}

function GlyphZeroOrOne({ color, sw }: GlyphProps) {
  const circCx = 2 + CIRC_R;
  return (
    <g>
      <line x1={0} y1={-BAR_H} x2={0} y2={BAR_H} stroke={color} strokeWidth={sw} />
      <circle cx={circCx} cy={0} r={CIRC_R} fill="white" stroke={color} strokeWidth={sw} />
    </g>
  );
}

function GlyphOneMany({ color, sw }: GlyphProps) {
  const barX = CROW_LEN + 2;
  return (
    <g>
      <line x1={CROW_LEN} y1={0} x2={0} y2={-CROW_SPREAD} stroke={color} strokeWidth={sw} />
      <line x1={CROW_LEN} y1={0} x2={0} y2={0} stroke={color} strokeWidth={sw} />
      <line x1={CROW_LEN} y1={0} x2={0} y2={CROW_SPREAD} stroke={color} strokeWidth={sw} />
      <line x1={barX} y1={-BAR_H} x2={barX} y2={BAR_H} stroke={color} strokeWidth={sw} />
    </g>
  );
}

function GlyphZeroOneMany({ color, sw }: GlyphProps) {
  const barX = CROW_LEN + 2;
  const circCx = barX + 2 + CIRC_R;
  return (
    <g>
      <line x1={CROW_LEN} y1={0} x2={0} y2={-CROW_SPREAD} stroke={color} strokeWidth={sw} />
      <line x1={CROW_LEN} y1={0} x2={0} y2={0} stroke={color} strokeWidth={sw} />
      <line x1={CROW_LEN} y1={0} x2={0} y2={CROW_SPREAD} stroke={color} strokeWidth={sw} />
      <line x1={barX} y1={-BAR_H} x2={barX} y2={BAR_H} stroke={color} strokeWidth={sw} />
      <circle cx={circCx} cy={0} r={CIRC_R} fill="white" stroke={color} strokeWidth={sw} />
    </g>
  );
}

function CardinalityGlyph({
  cardinality,
  handleX,
  handleY,
  position,
  color,
  strokeWidth
}: {
  cardinality: IECardinality;
  handleX: number;
  handleY: number;
  position: Position;
  color: string;
  strokeWidth: number;
}) {
  const [dx, dy] = posToOutward(position);
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const x = handleX + dx * INNER_GAP;
  const y = handleY + dy * INNER_GAP;

  let glyph;
  switch (cardinality) {
    case "1:1": glyph = <GlyphExactlyOne color={color} sw={strokeWidth} />; break;
    case "0:1": glyph = <GlyphZeroOrOne color={color} sw={strokeWidth} />; break;
    case "1:M": glyph = <GlyphOneMany color={color} sw={strokeWidth} />; break;
    case "0:1:M": glyph = <GlyphZeroOneMany color={color} sw={strokeWidth} />; break;
  }

  return (
    <g transform={`translate(${x},${y}) rotate(${deg})`}>
      {glyph}
    </g>
  );
}

// ── Edge component ──

export function IERelationshipEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd
}: EdgeProps<IERelationshipEdgeType>) {
  const { getEdges, getNodes } = useReactFlow();

  const srcCard: IECardinality = data?.source_cardinality ?? "0:1";
  const tgtCard: IECardinality = data?.target_cardinality ?? "0:1:M";
  const label = data?.label;

  const edges = getEdges();
  const allNodes = getNodes();
  const nodeMap = new Map(allNodes.map(n => [n.id, n]));
  const thisEdge = edges.find((x) => x.id === id);

  const sourceSiblings = thisEdge
    ? edges
        .filter(e => e.source === thisEdge.source && (e.sourceHandle ?? null) === (thisEdge.sourceHandle ?? null))
        .sort((a, b) => {
          const na = nodeMap.get(a.target);
          const nb = nodeMap.get(b.target);
          const axis = (sourcePosition === Position.Top || sourcePosition === Position.Bottom)
            ? ((na?.position.x ?? 0) - (nb?.position.x ?? 0))
            : ((na?.position.y ?? 0) - (nb?.position.y ?? 0));
          return axis || a.id.localeCompare(b.id);
        })
    : [];
  const srcFanIdx = sourceSiblings.findIndex(e => e.id === id);

  const targetSiblings = thisEdge
    ? edges
        .filter(e => e.target === thisEdge.target && (e.targetHandle ?? null) === (thisEdge.targetHandle ?? null))
        .sort((a, b) => {
          const na = nodeMap.get(a.source);
          const nb = nodeMap.get(b.source);
          const axis = (targetPosition === Position.Top || targetPosition === Position.Bottom)
            ? ((na?.position.x ?? 0) - (nb?.position.x ?? 0))
            : ((na?.position.y ?? 0) - (nb?.position.y ?? 0));
          return axis || a.id.localeCompare(b.id);
        })
    : [];
  const tgtFanIdx = targetSiblings.findIndex(e => e.id === id);

  const [spx, spy] = posToPerpendicular(sourcePosition);
  const srcFan = fanOffset(srcFanIdx, sourceSiblings.length);
  const sx = sourceX + spx * srcFan;
  const sy = sourceY + spy * srcFan;

  const [tpx, tpy] = posToPerpendicular(targetPosition);
  const tgtFan = fanOffset(tgtFanIdx, targetSiblings.length);
  const tx = targetX + tpx * tgtFan;
  const ty = targetY + tpy * tgtFan;

  const [sdx, sdy] = posToOutward(sourcePosition);
  const [tdx, tdy] = posToOutward(targetPosition);

  const srcStub = glyphExtent(srcCard);
  const tgtStub = glyphExtent(tgtCard);

  const inSx = sx + sdx * srcStub;
  const inSy = sy + sdy * srcStub;
  const inTx = tx + tdx * tgtStub;
  const inTy = ty + tdy * tgtStub;

  const [bezierSegment, labelX, labelY] = getBezierPath({
    sourceX: inSx,
    sourceY: inSy,
    sourcePosition,
    targetX: inTx,
    targetY: inTy,
    targetPosition,
    curvature: BEZIER_CURVATURE
  });

  const curveCmd = bezierSegment.substring(bezierSegment.indexOf("C"));
  const edgePath = `M ${sx},${sy} L ${inSx},${inSy} ${curveCmd} L ${tx},${ty}`;

  const strokeWidth = selected ? EDGE_WIDTH_HOVER : EDGE_WIDTH;
  const opacity = selected ? 1.0 : 0.8;
  const color = EDGE_COLOR;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ stroke: color, strokeWidth, opacity }}
      />
      <CardinalityGlyph
        cardinality={srcCard}
        handleX={sx}
        handleY={sy}
        position={sourcePosition}
        color={color}
        strokeWidth={strokeWidth}
      />
      <CardinalityGlyph
        cardinality={tgtCard}
        handleX={tx}
        handleY={ty}
        position={targetPosition}
        color={color}
        strokeWidth={strokeWidth}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: "white",
              padding: "2px 6px",
              borderRadius: 4,
              fontSize: 11,
              color: "var(--muted, #64748b)",
              border: "1px solid var(--line, #e2e8f0)",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              opacity: selected ? 1 : 0.7
            }}
            className="nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
